import { supabase } from './supabaseClient';
import { getViewingAs } from './viewingAs';

// fetch() that carries the current Supabase session as a bearer token.
//
// Backend routes derive the caller's identity from this token rather than from
// a client_id / customerId in the request, which is what stops one client from
// acting on another's data. Introduced for the api/ billing functions; the rest
// of the app's call sites move onto it in a later pass.
//
// getSession() returns the live token and supabase-js refreshes it in the
// background, so this always sends a current one without extra bookkeeping.
export async function authHeader() {
  try {
    const { data } = await supabase.auth.getSession();
    const token = data?.session?.access_token;
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    // Signed out, or storage unavailable. Send nothing and let the server
    // answer 401 — never fabricate a header or silently pass unauthenticated.
    return {};
  }
}

// ── Admin "act as client" routing ────────────────────────────────────────────
//
// THE PARAM NAME IS NOT A GUESS. requireOwnerOrAdmin (autopilot-backend
// server.js) reads the requested client from, in this precedence order:
//
//   req.body.clientId → req.body.client_id
//   → req.query.client → req.query.clientId → req.query.client_id
//   → req.params.clientId → req.params.client_id
//
// 'client' on the query string is used here because it is the first query
// spelling in that chain and the only one that works on every method without
// touching the request body: query survives GET (which has no body) and
// multipart (where requireOwnerOrAdmin runs before multer and so cannot see
// the body at all).
//
// TWO CONSEQUENCES OF THAT PRECEDENCE, both load-bearing for later phases:
//
//  1. QUERY BEATS PATH. Adding ?client=X to /api/usage/:clientId overrides the
//     path segment. That is the behaviour this feature wants, and it is also
//     why the override is opt-in per call (below) rather than automatic.
//
//  2. BODY BEATS QUERY. A POST that already sends its own clientId in the JSON
//     body wins over anything added here, silently. Phases 5-7 migrate the
//     write-side call sites and will hit this: those either drop the body's
//     clientId or set it to the viewed client explicitly. maybeWarnBodyConflict
//     below shouts when it sees the collision so it cannot be discovered as a
//     production bug.

const CLIENT_PARAM = 'client';

// Every query spelling requireOwnerOrAdmin accepts. If a caller already named
// a client in the URL, that is an explicit choice and is never overwritten.
const CLIENT_QUERY_KEYS = ['client', 'clientId', 'client_id'];

function maybeWarnBodyConflict(body, override) {
  // Only inspectable for a JSON string body. FormData/Blob/streams are skipped
  // rather than guessed at.
  if (typeof body !== 'string') return;
  try {
    const parsed = JSON.parse(body);
    if (!parsed || typeof parsed !== 'object') return;
    const named = parsed.clientId || parsed.client_id;
    if (named && named !== override) {
      console.warn(
        `[viewingAs] request body names client ${named} while viewing as ${override}. ` +
        'requireOwnerOrAdmin reads the body BEFORE the query string, so the body wins ' +
        'and the viewing-as override is being ignored on this call.',
      );
    }
  } catch {
    // Not JSON. Nothing to check.
  }
}

// Returns url with ?client=<viewed id> attached, or unchanged when there is
// nothing to attach. Relative URLs stay relative — the app calls authedFetch
// with both relative paths (through the Vercel rewrites) and absolute backend
// URLs, and turning the former into the latter would bypass those rewrites.
export function withViewingAsClient(url) {
  const { viewingAsClientId } = getViewingAs();
  if (!viewingAsClientId) return url;

  try {
    const isAbsolute = /^[a-z][a-z0-9+.-]*:\/\//i.test(String(url));
    const parsed = new URL(String(url), window.location.origin);

    // Caller already named a client: leave it alone. Anything else would make
    // this helper silently rewrite a deliberate request — including the root
    // shell's /api/customer?client=<own id>, which Phase 3 has not yet decided
    // the ownership of.
    if (CLIENT_QUERY_KEYS.some(k => parsed.searchParams.has(k))) return url;

    parsed.searchParams.set(CLIENT_PARAM, viewingAsClientId);
    return isAbsolute
      ? parsed.toString()
      : `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    // Unparseable URL. Return it untouched and let fetch report the real
    // problem, rather than masking it with one from here.
    return url;
  }
}

// options.viewAs — opt in to the act-as-client override for THIS call.
//
// Opt-in, not automatic, and that is deliberate. authedFetch already has live
// callers (/api/customer in the root shell, /api/usage in PostApprovalPanel),
// and an automatic override would change what those return the instant an
// admin clicked "View as" — silently migrating call sites that belong to
// Phases 2, 3 and 5. Opt-in makes Phase 1 provably behaviour-neutral for every
// existing call and lets later phases move sites over one at a time, each one
// greppable. Flipping the default once every site has moved is a one-line
// change here.
export async function authedFetch(url, options = {}) {
  const { viewAs = false, ...rest } = options;
  const auth = await authHeader();
  const target = viewAs ? withViewingAsClient(url) : url;
  if (viewAs) maybeWarnBodyConflict(rest.body, getViewingAs().viewingAsClientId);
  return fetch(target, { ...rest, headers: { ...(rest.headers || {}), ...auth } });
}

// authedFetch with the override on. This is the call panels move onto as they
// migrate off direct Supabase — `clientScopedFetch` in a diff is the marker
// that a call site has been through that migration.
export function clientScopedFetch(url, options = {}) {
  return authedFetch(url, { ...options, viewAs: true });
}
